import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import useOpenLabReport from 'src/labOrder/useOpenLabReport'
import { PatientHistoryEntry } from '../../api/useAdvancedPatientHistory'
import { advancedColors } from '../../theme'
import { useMarkLabResultsViewed } from '../../api/useMarkLabResultsViewed'
import { useDisabledInPreview } from '../../dashboard/panelPreviewContext'
import { FINANCIAL_ELIGIBILITY_LAYOUT, LayoutBody, VIDEO_CALL_LAYOUT } from './historyLayouts'
import HistoryValueTable from './HistoryValueTable'

// `value` exists on only two of the four concrete `PatientHistory` types; the others carry typed fields.
export const valueOf = (entry: PatientHistoryEntry): Record<string, unknown> | undefined => {
  if (entry.__typename === 'CommonPatientHistory' || entry.__typename === 'VideoCallPatientHistory') {
    return (entry.value as Record<string, unknown> | null) ?? undefined
  }

  return undefined
}

export const EntryFiles = ({ files }: { files: PatientHistoryEntry['files'] }) => {
  if (!files || files.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {files.map((file) =>
        file.downloadURL ? (
          <Typography
            key={file.fileID}
            variant="body2"
            component="a"
            href={file.downloadURL}
            target="_blank"
            rel="noreferrer"
            sx={{ color: advancedColors.green }}
          >
            {file.filename}
          </Typography>
        ) : (
          <Typography key={file.fileID} variant="body2" sx={{ color: advancedColors.inkMuted }}>
            {file.filename}
          </Typography>
        )
      )}
    </Box>
  )
}

const VIDEO_CALL_REASON_LABELS: Record<string, string> = {
  ENGAGEMENT_ISSUE: 'Program engagement',
  ESCALATING_SYMPTOMS: 'Escalating symptoms',
  LIFE_ISSUE: 'Life event(s)',
  OTHER_REASON: 'Other',
  PATIENT_INITIATED: 'Patient initiated',
}

const dollars = (cents: number | null | undefined) => (typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '—')

const LabOrderBody = ({ orderID }: { orderID: string | null | undefined }) => {
  const [openReport, openReportStatus] = useOpenLabReport()
  const openDisabled = useDisabledInPreview(openReportStatus.loading)
  useMarkLabResultsViewed(orderID)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Typography variant="body2" sx={{ color: advancedColors.inkMuted, mb: 0.5 }}>
          Order ID
        </Typography>
        <Typography variant="body2">{orderID ?? '—'}</Typography>
      </Box>
      {orderID && (
        <Box>
          <Button
            disabled={openDisabled}
            onClick={() => {
              openReport(orderID).catch(() => undefined)
            }}
          >
            Open results
          </Button>
        </Box>
      )}
    </Box>
  )
}

export const TypedFields = ({ entry }: { entry: PatientHistoryEntry }) => {
  if (entry.__typename === 'FinancialEligibilityPatientHistory') {
    const estimate = entry.costEstimate

    return (
      <LayoutBody
        sections={FINANCIAL_ELIGIBILITY_LAYOUT}
        source={{
          description: entry.description,
          ...(estimate
            ? {
                intake: dollars(estimate.intakeCostEstimateInCents),
                month1: dollars(estimate.month1CostEstimateInCents),
                month2: dollars(estimate.month2CostEstimateInCents),
                month3: dollars(estimate.month3CostEstimateInCents),
              }
            : null),
        }}
      />
    )
  }

  if (entry.__typename === 'LabOrderPatientHistory') {
    return <LabOrderBody orderID={entry.labOrder?.orderID} />
  }

  if (entry.__typename === 'VideoCallPatientHistory' && entry.videoCall) {
    const { reason, activeDurationInMS, numberOfParticipants } = entry.videoCall
    const notes = valueOf(entry)?.description

    return (
      <LayoutBody
        sections={VIDEO_CALL_LAYOUT}
        source={{
          participants: numberOfParticipants ?? 'N/A',
          duration: `${activeDurationInMS ? Math.floor(activeDurationInMS / 60000) : 0} minutes`,
          reason: (reason && VIDEO_CALL_REASON_LABELS[reason]) || 'UNKNOWN',
          notes: notes || 'N/A',
        }}
      />
    )
  }

  return null
}

export const DefaultBody = ({ entry }: { entry: PatientHistoryEntry }) => (
  <>
    <TypedFields entry={entry} />
    <HistoryValueTable value={valueOf(entry)} />
  </>
)
